import { createTheme } from "@mui/material/styles";

// Professional color palette
const palette = {
  primary: {
    main: "#2563eb", // Professional blue
    light: "#3b82f6",
    dark: "#1e40af",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#7c3aed", // Professional purple
    light: "#a78bfa",
    dark: "#6d28d9",
    contrastText: "#ffffff",
  },
  success: {
    main: "#10b981",
    light: "#6ee7b7",
    dark: "#059669",
  },
  warning: {
    main: "#f59e0b",
    light: "#fbbf24",
    dark: "#d97706",
  },
  error: {
    main: "#ef4444",
    light: "#f87171",
    dark: "#dc2626",
  },
  info: {
    main: "#06b6d4",
    light: "#22d3ee",
    dark: "#0891b2",
  },
  background: {
    default: "#f9fafb",
    paper: "#ffffff",
  },
  text: {
    primary: "#111827",
    secondary: "#6b7280",
    disabled: "#d1d5db",
  },
  divider: "#e5e7eb",
  action: {
    active: "#2563eb",
    hover: "#f3f4f6",
    selected: "#eff6ff",
    disabled: "#d1d5db",
    disabledBackground: "#f3f4f6",
  },
};

export const theme = createTheme({
  palette,
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2.5rem",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h2: {
      fontSize: "2rem",
      fontWeight: 700,
      lineHeight: 1.3,
      letterSpacing: "-0.005em",
    },
    h3: {
      fontSize: "1.75rem",
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: "1rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: "0.0125em",
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.43,
      letterSpacing: "0.0125em",
    },
    button: {
      fontWeight: 600,
      textTransform: "none",
      letterSpacing: "0.0046em",
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.66,
      letterSpacing: "0.0333em",
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 700,
      lineHeight: 2.66,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
          color: palette.text.primary,
        },
        "*::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: palette.background.default,
        },
        "*::-webkit-scrollbar-thumb": {
          backgroundColor: palette.action.disabled,
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: palette.text.secondary,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.primary.main,
          color: "#ffffff",
          boxShadow: "0 4px 12px 0 rgba(37, 99, 235, 0.15)",
          borderBottom: "none",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "64px",
          padding: "0 24px",
          "@media (max-width: 600px)": {
            minHeight: "56px",
            padding: "0 16px",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "8px",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        contained: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          "&:hover": {
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          },
        },
        sizeLarge: {
          padding: "12px 24px",
          fontSize: "1rem",
        },
        sizeMedium: {
          padding: "10px 20px",
          fontSize: "0.938rem",
        },
        sizeSmall: {
          padding: "6px 16px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
          borderRadius: "12px",
          border: `1px solid ${palette.divider}`,
          transition: "all 0.2s ease",
          "&:hover": {
            boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.08)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        elevation0: {
          boxShadow: "none",
        },
        elevation1: {
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            transition: "all 0.2s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.primary.light,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: palette.primary.main,
              borderWidth: "2px",
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          borderRadius: "8px",
          "&:hover": {
            backgroundColor: palette.action.hover,
          },
        },
        colorPrimary: {
          color: palette.primary.main,
          "&:hover": {
            backgroundColor: `${palette.primary.main}12`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          margin: "4px 0",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: palette.action.hover,
          },
          "&.Mui-selected": {
            backgroundColor: `${palette.primary.main}12`,
            "&:hover": {
              backgroundColor: `${palette.primary.main}1a`,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.paper,
          borderRight: `1px solid ${palette.divider}`,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          boxShadow: `0 0 0 2px ${palette.background.paper}`,
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

export default theme;
